import { useEffect, useMemo, useRef } from "react";
import { diffTemplates, type ReportTemplate } from "@broadpaper/core";
import { createReportPreview } from "@broadpaper/renderer";
import { useSession, useTemplate } from "../state/context.js";
import { useObservable } from "../state/observable.js";
import { Button } from "../ui/primitives.js";

/**
 * The proposal, over the canvas, until it is accepted or discarded.
 *
 * It draws the proposed template with the same preview pipeline the PDF uses,
 * in its own element, with the session's sample data — the store is never
 * touched to show it. Beside it, what would change, in the words the version
 * history uses, so "Accept" is a decision about something the person can see
 * rather than a leap.
 */
export function AiProposal() {
  const session = useSession();
  const status = useObservable(session.ai.state, (s) => s.status);
  // Subscribing to the document is what makes a stale proposal notice the edit.
  const template = useTemplate();
  if (status.phase !== "proposal") return null;
  const stale = template !== status.base;
  return (
    <section className="bpe-ai-proposal" aria-label="Proposed design">
      <ProposedPages template={status.template} />
      <aside className="bpe-ai-proposal__side">
        <h2 className="bpe-ai-proposal__title">{status.mode === "create" ? "Proposed new report" : "Proposed changes"}</h2>
        {status.notes ? <p className="bpe-ai-proposal__notes">{status.notes}</p> : null}
        <Changes before={status.base} after={status.template} />
        {stale ? (
          <p className="bpe-ai-proposal__stale" role="alert">
            The design changed while this was being made, so accepting it would undo your edits. Ask again to get a proposal made from the design as it is now.
          </p>
        ) : (
          <p className="bpe-muted bpe-ai-proposal__undo">Accepting is one step you can undo.</p>
        )}
        <div className="bpe-ai-proposal__actions">
          {stale ? (
            <Button
              variant="primary"
              onClick={() => {
                session.ai.discard();
                void session.ai.generate();
              }}
            >
              Ask again
            </Button>
          ) : (
            <Button variant="primary" icon="check" onClick={() => session.ai.accept()}>
              Accept
            </Button>
          )}
          <Button onClick={() => session.ai.discard()}>Discard</Button>
        </div>
      </aside>
    </section>
  );
}

function ProposedPages({ template }: { template: ReportTemplate }) {
  const session = useSession();
  const hostRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const options = session.options.get();
    let preview: ReturnType<typeof createReportPreview> | null = null;
    try {
      preview = createReportPreview(el, {
        template,
        registry: session.registry,
        theme: session.activeTheme,
        dataSources: session.dataSources,
        data: session.activeSample,
        mode: "preview",
        locale: options.locale,
        currency: options.currency,
        timeZone: options.timeZone,
        now: options.now,
        zoom: 0.75
      });
    } catch {
      // A preview that cannot be drawn still leaves the change list to decide by.
    }
    return () => preview?.destroy();
  }, [session, template]);
  return <div ref={hostRef} className="bpe-ai-proposal__pages" />;
}

function Changes({ before, after }: { before: ReportTemplate; after: ReportTemplate }) {
  const session = useSession();
  const diff = useMemo(() => diffTemplates(before, after, { registry: session.registry, dataSources: session.dataSources, maxChanges: 60 }), [before, after, session]);
  if (diff.identical) return <p className="bpe-muted">Nothing would change.</p>;
  return (
    <ul className="bpe-ai-proposal__changes">
      {diff.changes.map((c, i) => (
        <li key={`${c.nodeId ?? c.area}-${i}`} data-kind={c.kind}>
          {c.summary}
        </li>
      ))}
      {diff.truncated ? <li className="bpe-muted">…and {diff.truncated} more</li> : null}
    </ul>
  );
}
